import { type UseFormReturn } from "react-hook-form"
import { type IOffer } from "@commonTypes/offer"
import type { IOrderPayload } from "@commonTypes/order"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { Input } from "@components/ui/input"
import { Checkbox } from "@components/ui/checkbox"
import {
  useGetStatesQuery,
  useGetRevenueClassesQuery,
} from "@apis/shared/useShared"
import { useGetSalesLocationsQuery } from "@apis/salesLocations/useSalesLocations"
import { useGetAlternateOffersQuery } from "@apis/orders/useOrders"
import { useHasPermissions } from "@hooks/useHasPermissions"
import { Alert, AlertDescription, AlertTitle } from "@components/ui/alert"
import { Tooltip, TooltipTrigger, TooltipContent } from "@components/ui/tooltip"
import { AlertCircleIcon } from "lucide-react"

type EditOrderFormProps = {
  form: UseFormReturn<IOrderPayload & { market_code: string }>
  order_payload: IOrderPayload
  offer?: IOffer
}

export const EditOrderForm = ({
  form,
  order_payload,
  offer,
}: EditOrderFormProps) => {
  const { canSkipDuplicateAccountValidation } = useHasPermissions()

  const { data: alternateOffers = [] } = useGetAlternateOffersQuery(
    order_payload.order_id as number,
  )
  const { data: states = [] } = useGetStatesQuery()
  const { data: revenueClasses = [] } = useGetRevenueClassesQuery()
  const { data: salesLocations = [] } = useGetSalesLocationsQuery({
    active: false,
  })

  const allOffers = [
    ...(offer && !alternateOffers.some((o) => o.offer_code === offer.offer_code)
      ? [offer]
      : []),
    ...alternateOffers,
  ]

  const offerOptions = allOffers.map((offer) => ({
    value: offer.offer_code,
    label: `${offer.market_code} (${offer.offer_desc})`,
    offer_code: offer.offer_code,
  }))

  const salesLocationsOptions = salesLocations.map((salesLocation) => ({
    value: salesLocation.location_code,
    label: salesLocation.location_name,
  }))

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="tw:space-y-6">
        <div className="tw:flex tw:flex-col tw:gap-0.5 tw:font-medium">
          <FormLabel htmlFor="sale_dt">Sale Date</FormLabel>
          <p id="sale_dt" className="mb-0">
            {order_payload.sale_dt}
          </p>
        </div>
        <div className="tw:grid tw:grid-cols-2 tw:gap-4">
          <FormField
            control={form.control}
            name="email"
            rules={{ required: "Email is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="Enter email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="primary_phone"
            rules={{
              required: "Primary phone is required",
              pattern: {
                value: /^\d{10}$/,
                message: "Phone must be a 10-digit number",
              },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Primary Phone</FormLabel>
                <FormControl>
                  <Input placeholder="1234567890" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:grid tw:grid-cols-2 tw:gap-4">
          <FormField
            control={form.control}
            name="first_name"
            rules={{ required: "First name is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="last_name"
            rules={{ required: "Last name is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <hr />
        <div className="tw:grid tw:grid-cols-2 tw:gap-4">
          <FormField
            control={form.control}
            name="service_address_line_1"
            rules={{ required: "Service address is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service Line 1</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="service_address_line_2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service Line 2</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="tw:grid tw:grid-cols-4 tw:gap-4">
          <div className="tw:col-span-2 tw:grid">
            <FormField
              control={form.control}
              name="service_city"
              rules={{ required: "Service city is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service City</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {states.length > 0 && (
            <FormField
              control={form.control}
              name="service_state"
              rules={{ required: "Service state is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service State</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={typeof field.value === "string" ? field.value : ""}
                  >
                    <FormControl className="tw:w-full">
                      <SelectTrigger>
                        <SelectValue placeholder="Select State" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {states.map(
                        (option: { value: string; label: string }) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name="service_zip_code"
            rules={{ required: "Service ZIP code is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service ZIP</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <hr />
        <div className="tw:grid tw:grid-cols-2 tw:gap-4">
          <FormField
            control={form.control}
            name="billing_address_line_1"
            rules={{ required: "Billing address is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billing Line 1</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="billing_address_line_2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billing Line 2</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="tw:grid tw:grid-cols-4 tw:gap-4">
          <div className="tw:col-span-2 tw:grid">
            <FormField
              control={form.control}
              name="billing_city"
              rules={{ required: "Billing city is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Billing City</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {states.length > 0 && (
            <FormField
              control={form.control}
              name="billing_state"
              rules={{ required: "Billing state is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Billing State</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || ""}
                  >
                    <FormControl className="tw:w-full">
                      <SelectTrigger>
                        <SelectValue placeholder="Select State" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {states.map(
                        (option: { value: string; label: string }) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name="billing_zip_code"
            rules={{ required: "Billing ZIP code is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billing ZIP</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <hr />
        <div className="tw:grid tw:grid-cols-4">
          <div className="tw:col-span-2 tw:grid">
            <FormField
              control={form.control}
              name="account_number"
              rules={{
                pattern: offer?.validation_rules?.account_number_regexp
                  ? {
                      value: new RegExp(
                        offer.validation_rules.account_number_regexp,
                      ),
                      message: "Invalid account number format",
                    }
                  : undefined,
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Number</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <p className="tw:mb-0! tw:grid tw:h-[36px] tw:place-content-center tw:place-self-stretch tw:self-end tw:rounded-r tw:bg-gray-100 tw:text-gray-700">
            {offer?.validation_rules.account_number_example}
          </p>
        </div>
        <FormField
          control={form.control}
          name="has_account_number"
          render={({ field }) => (
            <FormItem className="tw:mt-2">
              <FormControl>
                <div className="tw:flex tw:items-center tw:gap-3">
                  <FormLabel>Has Account Number?</FormLabel>
                  <Checkbox
                    id="has_account_number"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="tw:flex tw:w-1/2 tw:flex-col tw:gap-4">
          <div>
            <div className="tw:grid tw:grid-cols-4">
              <FormField
                control={form.control}
                name="offer_code"
                rules={{ required: "Offer Code is required" }}
                render={({ field }) => (
                  <FormItem className="tw:col-span-3">
                    <FormLabel>Offer Code</FormLabel>
                    <FormControl>
                      <Input {...field} readOnly />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <p className="tw:mb-0! tw:grid tw:h-[36px] tw:place-content-center tw:place-self-stretch tw:self-end tw:rounded-r tw:bg-gray-100 tw:text-gray-700">
                ${offer?.rate}
              </p>
            </div>
            {offerOptions.length > 0 && (
              <FormField
                control={form.control}
                name="offer_code"
                rules={{ required: "Offer Code is required" }}
                render={({ field }) => (
                  <FormItem>
                    <Select
                      onValueChange={(selectedMarketCode) => {
                        field.onChange(selectedMarketCode)

                        const selected = offerOptions.find(
                          (o) => o.value === selectedMarketCode,
                        )
                        if (selected?.offer_code) {
                          form.setValue("offer_code", selected.offer_code)
                        }
                      }}
                      value={field.value || ""}
                    >
                      <FormControl className="tw:w-full">
                        <SelectTrigger>
                          <SelectValue placeholder="Select Market" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {offerOptions.map(
                          (option: { value: string; label: string }) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
          {revenueClasses.length > 0 && (
            <FormField
              control={form.control}
              name="revenue_class_code"
              rules={{
                validate: (val) => val !== "" || "Revenue Class is required",
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Revenue Class</FormLabel>
                  <Select
                    onValueChange={(val) =>
                      field.onChange(val === "__none__" ? "" : val)
                    }
                    value={field.value || "__none__"}
                  >
                    <FormControl className="tw:w-full">
                      <SelectTrigger>
                        <SelectValue placeholder="Select Revenue Class" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem key="None" value="__none__">
                        None
                      </SelectItem>
                      {revenueClasses.map(
                        (option: { value: string; label: string }) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          {salesLocationsOptions.length > 0 && (
            <FormField
              control={form.control}
              name="sales_location_code"
              rules={{
                validate: (val) => val !== "" || "Sales Location is required",
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sales Location</FormLabel>
                  <Select
                    onValueChange={(val) =>
                      field.onChange(val === "__none__" ? "" : val)
                    }
                    value={field.value || "__none__"}
                  >
                    <FormControl className="tw:w-full">
                      <SelectTrigger>
                        <SelectValue placeholder="Select Location" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem key="None" value="__none__">
                        None
                      </SelectItem>
                      {salesLocationsOptions.map(
                        (option: { value: string; label: string }) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
        {canSkipDuplicateAccountValidation && (
          <div className="tw:grid tw:grid-cols-4 tw:gap-4">
            <Alert variant="destructive" className="tw:col-start-4">
              <AlertCircleIcon />
              <AlertTitle>Override Options</AlertTitle>
              <AlertDescription>
                <FormField
                  control={form.control}
                  name="skip_duplicate_account_validation"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="tw:flex tw:items-center tw:gap-3">
                              <Checkbox
                                id="skip_duplicate_account_validation"
                                checked={field.value ?? false}
                                onCheckedChange={field.onChange}
                              />
                              <FormLabel
                                htmlFor="skip_duplicate_account_validation"
                                className="tw:cursor-pointer tw:font-medium"
                              >
                                Skip duplicate account check
                              </FormLabel>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            Use only when the duplicate is intentional.
                          </TooltipContent>
                        </Tooltip>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </AlertDescription>
            </Alert>
          </div>
        )}
      </form>
    </Form>
  )
}
